import React, { useState } from 'react';
import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Select,
    VStack,
    useToast,
    RadioGroup,
    HStack,
    Radio,
} from '@chakra-ui/react';
import api from '../../../auth/apiClient';
import * as XLSX from 'xlsx-js-style';
import { trackDownload } from '../../../activity';

interface ReportPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

const ReportPopupProduksi: React.FC<ReportPopupProps> = ({ isOpen, onClose }) => {
    const [reportType, setReportType] = useState<'ytd' | 'mtd'>('ytd');
    const [selectedMonth, setSelectedMonth] = useState<string>('');
    const [selectedEstate, setSelectedEstate] = useState<string>('');
    const [selectedCompany, setSelectedCompany] = useState<string>('1');
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();

    // Generate months for the last 12 months
    const months = Array.from({ length: 12 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        return {
            value: `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`,
            label: `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`
        };
    });

    const handleExport = async () => {
        if (!selectedMonth) {
            toast({
                title: 'Error',
                description: 'Please select month',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsLoading(true);
        try {
            const selectedYear = parseInt(selectedMonth.substring(0, 4));
            const baseUrl = '/odoo/api/report-produksi/monthly';

            let url = baseUrl;
            const params: any = {};

            if (reportType === 'mtd') {
                // url += '/monthly';

                params.start_period = selectedMonth;
                params.end_period = selectedMonth;
            } else {
                params.start_period = `${selectedYear}01`;
                params.end_period = selectedMonth;
            }
            if (selectedCompany) params.company_id = selectedCompany;
            if (selectedEstate) params.estate = selectedEstate;
            console.log('Request URL & params:', url, params);

            const response = await api.get(url, { params });

            // Transform data for Excel
            const excelData = response.data.data.map((item: any) => ({
                'periode': item.periode,
                'Estate': item.estate,
                'Divisi': item.division,
                'Blok': item.block,
                'Status Blok': item.block_status,
                'Tahun Tanam': item.planted_year,
                'Luas Tanam (Ha)': item.planted_area,
                'Total Tanam': item.total_planted,
                'SPH': item.sph,
                '% SPH': item.sph / 136,
                'Luas Panen (Ha)': item.harvest_area,
                'Rotasi': item.rotation,
                'HK Aktual': item.hk_act,
                'HK Budget': item.hk_bgt,
                '% HK': item.hk_bgt ? (item.hk_act / item.hk_bgt) : 0,
                'JJG Aktual': item.jjg_act,
                'JJG Budget': item.jjg_bgt,
                '% JJG': item.jjg_bgt ? (item.jjg_act / item.jjg_bgt) : 0,
                'Berat Aktual (kg)': item.weight_act,
                'Berat Budget (kg)': item.weight_bgt,
                '% Berat': item.weight_bgt ? (item.weight_act / item.weight_bgt) : 0,
                'Yield Aktual (ton)': item.yield_act / 1000,
                'Yield Budget (ton)': item.yield_bgt / 1000,
                '% Yield': item.yield_bgt ? ((item.yield_act / item.yield_bgt) * 100) : 0,
                'BJR Aktual': item.bjr_act,
                'BJR Budget': item.bjr_bgt,
                '% BJR': item.bjr_bgt ? (item.bjr_act / item.bjr_bgt) : 0,
                'Brondolan Aktual': item.brd_qty_act,
                '% Brondolan': item.weight_act ? (item.brd_qty_act / item.weight_act) : 0,
                // '% Lebih Basis': item.lebih_basis_prc,
                'Biaya Panen': item.upah_panen_cost,
                'Biaya Premi': item.premi_panen_cost,
                'Biaya Kontraktor': item.upah_kontraktor_cost,
                'Biaya Taksasi': item.taksasi_cost,
                'Biaya Kutip Brondol': item.kutip_brondolan_cost,
                'Biaya Muat': item.tukang_muat_cost,
                'Biaya Transport': item.transport_cost,
                'Biaya Peralatan': item.peralatan_panen_cost,
                'Biaya Supervisi': item.supervisi_cost,
                'Biaya Lain': item.other_cost,
                'Total Biaya': item.total_cost,
                'Rp/kg': item.cost_per_kg
            }));

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Set column widths
            const colWidths = [
                { wch: 22 }, // Periode
                { wch: 22 }, // Estate
                { wch: 20 }, // Divisi
                { wch: 15 }, // Blok
                { wch: 15 }, // Status Blok
                { wch: 12 }, // Tahun Tanam
                { wch: 15 }, // Luas Tanam
                { wch: 12 }, // Total Tanam
                { wch: 8 },  // SPH
                { wch: 8 },  // % SPH
                { wch: 15 }, // Luas Panen
                { wch: 10 }, // Rotasi
                { wch: 12 }, // HK Aktual
                { wch: 12 }, // HK Budget
                { wch: 8 },  // % HK
                { wch: 12 }, // JJG Aktual
                { wch: 12 }, // JJG Budget
                { wch: 8 },  // % JJG
                { wch: 15 }, // Berat Aktual
                { wch: 15 }, // Berat Budget
                { wch: 10 }, // % Berat
                { wch: 14 }, // Yield Aktual
                { wch: 14 }, // Yield Budget
                { wch: 10 }, // % Yield
                { wch: 12 }, // BJR Aktual
                { wch: 12 }, // BJR Budget
                { wch: 8 },  // % BJR
                { wch: 15 }, // Brondolan Aktual
                { wch: 12 }, // % Brondolan
                // { wch: 12 }, // % Lebih Basis
                { wch: 14 }, // Biaya Panen
                { wch: 12 }, // Biaya Premi
                { wch: 16 }, // Biaya Kontraktor
                { wch: 13 }, // Biaya Taksasi
                { wch: 18 }, // Biaya Kutip Brondol
                { wch: 12 }, // Biaya Muat
                { wch: 15 }, // Biaya Transport
                { wch: 15 }, // Biaya Peralatan
                { wch: 14 }, // Biaya Supervisi
                { wch: 12 }, // Biaya Lain
                { wch: 12 }, // Total Biaya
                { wch: 10 }  // Rp/kg
            ];
            ws['!cols'] = colWidths;
            ws['!sheetView'] = [{ showGridLines: false }];

            // Add styling
            const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

            // Header styling
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cell = XLSX.utils.encode_cell({ r: 0, c: C });
                if (!ws[cell]) ws[cell] = { v: '' };
                ws[cell].s = {
                    font: { bold: true, color: { rgb: "FFFFFF" } },
                    fill: { type: "pattern", pattern: "solid", fgColor: { rgb: "4A5A33" } },
                    alignment: { horizontal: "center", vertical: "center" },
                    border: {
                        top: { style: "thin", color: { rgb: "000000" } },
                        bottom: { style: "thin", color: { rgb: "000000" } },
                        left: { style: "thin", color: { rgb: "000000" } },
                        right: { style: "thin", color: { rgb: "000000" } }
                    }
                };
            }

            // Definisikan kelompok yang mau di-outline
            const groups = [
                { start: 1, end: 5 }, // Estate (0) … Tahun Tanam (4)
                { start: 6, end: 9 }, // Luas Tanam (6) … % SPH (9)
                { start: 10, end: 11 }, // Luas Panen (9) … Rotasi (10)
                { start: 12, end: 14 }, // HK Aktual (11) … % HK (13)
                { start: 15, end: 17 }, // JJG Aktual (14) … % JJG (16)
                { start: 18, end: 20 }, // Berat Aktual (17) … % Berat (19)
                { start: 21, end: 23 }, // Yield Aktual (20) … % Yield (22)
                { start: 24, end: 26 }, // BJR Aktual (23) … % BJR (25)
                { start: 27, end: 28 }, // Brondolan Aktual (26) … % Brondolan (28)
                { start: 29, end: 40 }  // Biaya Panen (29) … Rp/kg (40)
            ];

            // 2. Hitung baris pertama & terakhir
            const firstRow = range.s.r;      // 0 (header)
            const lastRow = range.e.r;      // excelData.length

            // Data cell styling
            groups.forEach(({ start, end }) => {
                for (let R = firstRow; R <= lastRow; ++R) {
                    for (let C = start; C <= end; ++C) {
                        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
                        if (!ws[cellRef]) ws[cellRef] = { v: '' };

                        // Apply number formatting for numeric columns
                        if (C >= 5) { // All columns after Tahun Tanam are numeric
                            // Check if this is a percentage column by looking at the column index
                            const isPercentageColumn = [
                                9,  // % SPH
                                14, // % HK
                                17, // % JJG
                                20, // % Berat
                                22, // % Yield
                                26, // % BJR
                                28, // % Brondolan
                                // 28  // % Lebih Basis
                            ].includes(C);
                            
                            // no decimal: total tanam, sph, jjg_act, jjg_bgt, berat_act, berat_bgt, brd_qty_act, biaya (except rp/kg)
                            const isNoDecimalColumn = [
                                7,  // Total Tanam
                                8,  // SPH
                                12, // HK Aktual
                                13, // HK Budget
                                15, // JJG Aktual
                                16, // JJG Budget
                                18, // Berat Aktual
                                19, // Berat Budget
                                27, // Brondolan Aktual
                                29, // Biaya Panen
                                30, // Biaya Premi
                                31, // Biaya Kontraktor
                                32, // Biaya Taksasi
                                33, // Biaya Kutip Brondol
                                34, // Biaya Muat
                                35, // Biaya Transport
                                36, // Biaya Peralatan
                                37, // Biaya Supervisi
                                38, // Biaya Lain
                                39  // Total Biaya
                            ].includes(C);

                            if (isPercentageColumn) {
                                ws[cellRef].z = '0.00%';
                            } else if (isNoDecimalColumn) {
                                ws[cellRef].z = '#,##0;[Red]-#,##0;"-"';
                            } else {
                                ws[cellRef].z = '#,##0.00;[Red]-#,##0.00;"-"';
                            }
                        }

                        const border: any = {};

                        // Top outline
                        if (R === firstRow) {
                            border.top = { style: 'thin', color: { rgb: '000000' } };
                        }
                        // Bottom outline
                        if (R === lastRow) {
                            border.bottom = { style: 'thin', color: { rgb: '000000' } };
                        }
                        // Left outline
                        if (C === start) {
                            border.left = { style: 'thin', color: { rgb: '000000' } };
                        }
                        // Right outline
                        if (C === end) {
                            border.right = { style: 'thin', color: { rgb: '000000' } };
                        }

                        // Apply ke sel
                        ws[cellRef].s = {
                            ...(ws[cellRef].s || {}),  // pertahankan style lain (alignment, fill, dsb)
                            border
                        };
                    }
                }
            });

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Report');

            // Generate Excel file
            XLSX.writeFile(wb, `produksi_report_${selectedMonth}.xlsx`);
            trackDownload('Laporan Produksi', { month: selectedMonth });

            toast({
                title: 'Success',
                description: 'Report exported successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error exporting report:', error);
            toast({
                title: 'Error',
                description: 'Failed to export report',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Download Report Produksi</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <VStack spacing={4}>
                        <FormControl>
                            <FormLabel>Tipe Laporan</FormLabel>
                            <RadioGroup
                                onChange={val => setReportType(val as 'ytd' | 'mtd')}
                                value={reportType}
                            >
                                <HStack spacing={6}>
                                    <Radio value="ytd">Year to Date</Radio>
                                </HStack>
                            </RadioGroup>
                        </FormControl>
                        <FormControl>
                            <FormLabel>Bulan</FormLabel>
                            <Select
                                placeholder="Pilih bulan"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                            >
                                {months.map((month) => (
                                    <option key={month.value} value={month.value}>
                                        {month.label}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Company</FormLabel>
                            <Select
                                value={selectedCompany}
                                onChange={(e) => setSelectedCompany(e.target.value)}
                            >
                                <option value="1">PT Golden Blossom Sumatra</option>
                                <option value="2">PT Laras Karya Kahirupan</option>
                            </Select>
                        </FormControl>

                        <Button
                            colorScheme="green"
                            onClick={handleExport}
                            isLoading={isLoading}
                            loadingText="Exporting..."
                            width="full"
                        >
                            Download
                        </Button>
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default ReportPopupProduksi;